import {
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

import { Form, Formik, FormikErrors, FormikHelpers } from "formik";

import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material ui Icon
import { setMessage, useMaterialUIController } from "context";

// Data
import { getAllOrganizador } from "services/organizador";
import { editEventos, getTypeSystem, saveEventos } from "services/evento";

import {
  currencyToNumber,
  parseBoolean,
  resolveApiMessage,
} from "helpers/utils";

import { getSchemaCadastroEvento } from "./schemas/validations";
import formCadastroEvento from "./schemas/forms";

import BannerEvento from "../BannerEvento";

import { Organizador } from "interfaces/organizador";
import { IEventoResponse } from "interfaces/eventos";

import { PagesEvento } from "../..";
import FieldDadosEvento from "./FieldFormEvento/FieldDadosEvento";
import FieldDetails from "./FieldFormEvento/FieldDetails";
import FieldTaxaAdm from "./FieldFormEvento/FieldTaxaAdm";
import FieldLinkDivulgacao from "./FieldFormEvento/FieldLinkDivulgacao";
import FieldRegulamento from "./FieldFormEvento/FieldRegulamento";
import FieldTermoResp from "./FieldFormEvento/FieldTermoResp";
import FieldInfoComplementar from "./FieldFormEvento/FieldInfoComplementar";
import FieldInfokits from "./FieldFormEvento/FieldInfokits";
import FieldMaps from "./FieldFormEvento/FieldMaps";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { Modalidade, StatusEvento, TipoServico } from "interfaces/typesystem";

interface DadosEventoProps {
  evento: IEventoResponse;
  updateEvento: () => void;
  gotoPage: (page: PagesEvento) => void;
  valueForm: any;
  isEdit?: boolean;
}

type FormErrorEntry = { field: string; message: string };

function flattenFormikErrors(
  errors: FormikErrors<any>,
  prefix = ""
): FormErrorEntry[] {
  const result: FormErrorEntry[] = [];

  for (const [key, value] of Object.entries(errors)) {
    const field = prefix ? `${prefix}.${key}` : key;

    if (typeof value === "string" && value) {
      result.push({ field, message: value });
      continue;
    }

    if (value && typeof value === "object") {
      result.push(...flattenFormikErrors(value as FormikErrors<any>, field));
    }
  }

  return result;
}

function scrollToFormField(fieldName: string): boolean {
  const fieldKey = fieldName.split(".").pop() || fieldName;
  const selectors = [
    `[name="${fieldKey}"]`,
    `[name="${fieldName}"]`,
    `#${fieldKey}`,
  ];

  for (const selector of selectors) {
    const element = document.querySelector(selector) as HTMLElement | null;
    if (!element) continue;

    element.scrollIntoView({ behavior: "smooth", block: "center" });

    const focusTarget =
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement
        ? element
        : (element.querySelector("input, textarea, select") as
            | HTMLElement
            | null);

    window.setTimeout(() => focusTarget?.focus?.(), 350);
    return true;
  }

  return false;
}

interface FormSubmitFeedbackProps {
  errors: FormikErrors<any>;
  submitCount: number;
  submitError: string;
  feedbackRef: RefObject<HTMLDivElement>;
}

function FormSubmitFeedback({
  errors,
  submitCount,
  submitError,
  feedbackRef,
}: FormSubmitFeedbackProps) {
  const [, dispatch] = useMaterialUIController();
  const lastHandledSubmit = useRef(0);
  const validationErrors = flattenFormikErrors(errors);
  const showValidation = submitCount > 0 && validationErrors.length > 0;

  useEffect(() => {
    if (submitCount === 0 || submitCount === lastHandledSubmit.current) return;

    const entries = flattenFormikErrors(errors);
    if (entries.length === 0) return;

    lastHandledSubmit.current = submitCount;
    const firstError = entries[0];

    setMessage(dispatch, {
      open: true,
      severity: "warning",
      text: firstError.message,
    });

    if (!scrollToFormField(firstError.field)) {
      feedbackRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [dispatch, errors, feedbackRef, submitCount]);

  useEffect(() => {
    if (!submitError) return;

    feedbackRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [feedbackRef, submitError]);

  if (!showValidation && !submitError) return null;

  return (
    <MDBox ref={feedbackRef} mb={2}>
      {submitError && (
        <MDBox mb={showValidation ? 2 : 0}>
          <MDAlert color="error">{submitError}</MDAlert>
        </MDBox>
      )}

      {showValidation && (
        <MDAlert color="warning">
          <MDTypography variant="button" fontWeight="medium" display="block">
            Não foi possível salvar. Corrija os campos indicados:
          </MDTypography>
          <MDBox component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {validationErrors.slice(0, 5).map(({ field, message }) => (
              <MDBox component="li" key={field} sx={{ mb: 0.5 }}>
                <MDTypography variant="caption" color="inherit">
                  {message}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
          {validationErrors.length > 5 && (
            <MDTypography variant="caption" color="inherit" display="block" mt={1}>
              ... e mais {validationErrors.length - 5} campo(s) com pendências.
            </MDTypography>
          )}
        </MDAlert>
      )}
    </MDBox>
  );
}

export default function DadosEvento({
  evento,
  updateEvento,
  valueForm,
  isEdit,
}: DadosEventoProps) {
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();
  const [destaque, setDestaque] = useState<boolean>(
    parseBoolean(valueForm.exibirDestaque)
  );
  const [mapsExibir, setMapsExibir] = useState<boolean>(
    parseBoolean(valueForm.exibirMap)
  );
  const [isentarMorador, setIsentarMorador] = useState<boolean>(
    parseBoolean(valueForm.isentarMorador)
  );
  const [listOrganizador, setListOrganizador] = useState<Organizador[]>([]);
  const [listModalidade, setListModalidade] = useState<Modalidade[]>([]);
  const [listTipoServico, setListTipoServico] = useState<TipoServico[]>([]);
  const [listStatusEvento, setListStatusEvento] = useState<StatusEvento[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const imgUrlBanner = valueForm.urlBanner;
  const imgUrlCard = valueForm.urlImageDestaque;
  const [fileBanner, setFileBanner] = useState<globalThis.File | null>(null);
  const [fileCard, setFileCard] = useState<globalThis.File | null>(null);
  const [fileRegulamento, setFileRegulamento] =
    useState<globalThis.File | null>(null);
  const [fileTermoResp, setFileTermoResp] = useState<globalThis.File | null>(
    null
  );
  const [fileResumo, setFileResumo] = useState<globalThis.File | null>(null);
  const submitFeedbackRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerInView, setFooterInView] = useState(false);

  const hrefEvento = "/layouts/pages/cadastros/eventos";

  useEffect(() => {
    setDestaque(parseBoolean(valueForm.exibirDestaque));
    setMapsExibir(parseBoolean(valueForm.exibirMap));
    setIsentarMorador(parseBoolean(valueForm.isentarMorador));
  }, [valueForm.exibirDestaque, valueForm.exibirMap, valueForm.isentarMorador]);

  useEffect(() => {
    (async () => {
      setIsLoadingOptions(true);
      setOptionsError("");
      try {
        const [organizadorResponse, typeSystemResponse] = await Promise.all([
          getAllOrganizador(),
          getTypeSystem(),
        ]);

        if (
          organizadorResponse.status !== 200 ||
          typeSystemResponse.status !== 200
        ) {
          throw new Error("Falha ao carregar as opções do formulário.");
        }

        const organizador = organizadorResponse.data;
        const typeSystem = typeSystemResponse.data;
        if (organizador && Array.isArray(organizador)) {
          setListOrganizador(organizador);
        }

        setListModalidade(typeSystem.modalidade || []);
        setListTipoServico(typeSystem.tipoServico || []);
        setListStatusEvento(typeSystem.statusEvento || []);
      } catch (error) {
        setOptionsError(
          "Não foi possível carregar organizadores, modalidades e tipos de serviço."
        );
      } finally {
        setIsLoadingOptions(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setFooterInView(false);
      return;
    }

    const footerEl = footerRef.current;
    if (!footerEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => setFooterInView(entry.isIntersecting),
      { threshold: 0.35, rootMargin: "0px 0px -16px 0px" }
    );

    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [isEdit, valueForm]);

  const handlerExibirDestaque = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    setDestaque(!destaque);
    setFieldValue("exibirDestaque", destaque ? 0 : 1);
  };

  const handlerExibirMaps = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    setMapsExibir(!mapsExibir);
    setFieldValue("exibirMap", mapsExibir ? 0 : 1);
  };

  const handlerIsentarMorador = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const next = !isentarMorador;
    setIsentarMorador(next);
    setFieldValue("isentarMorador", next ? 1 : 0);
  };

  const handlerFileBanner = (file: File | null) => setFileBanner(file);
  const handlerFileCard = (file: File | null) => setFileCard(file);
  const clearFiles = () => {
    setFileBanner(null);
    setFileCard(null);
    setFileRegulamento(null);
    setFileTermoResp(null);
    setFileResumo(null);
  };

  const validatePdfFile = (file: File) => {
    if (file.type !== "application/pdf") {
      return "Selecione um arquivo PDF válido.";
    }
    if (file.size > 10 * 1024 * 1024) {
      return "O arquivo PDF deve ter no máximo 10 MB.";
    }
    return "";
  };

  const setPdfFile = (
    event: ChangeEvent<HTMLInputElement>,
    setter: (file: File | null) => void
  ) => {
    const { files } = event.currentTarget;
    if (files) {
      const fileSeled = files[0];
      if (fileSeled) {
        const error = validatePdfFile(fileSeled);
        if (error) {
          event.currentTarget.value = "";
          setMessage(dispatch, {
            open: true,
            text: error,
            severity: "error",
          });
          return;
        }
        setter(fileSeled);
      }
    }
  };

  const handlerFileReg = (event: ChangeEvent<HTMLInputElement>) =>
    setPdfFile(event, setFileRegulamento);

  const handlerDeleteFileReg = () => {
    if (isEdit) {
      // Verificar se possui um regulamento salvo
      if (evento?.urlRegulamento) {
        // Excluir a referencia do arquivo no banco de dados
        // Excluir do supabase repositorio do arquivo
      }
    }
    setFileRegulamento(null);
  };

  const handlerFileTerm = (event: ChangeEvent<HTMLInputElement>) =>
    setPdfFile(event, setFileTermoResp);

  const handlerDeleteFileTerm = () => setFileTermoResp(null);

  const handlerFileResumo = (event: ChangeEvent<HTMLInputElement>) =>
    setPdfFile(event, setFileResumo);

  const handlerDeleteFileResumo = () => setFileResumo(null);

  const getBackendFieldErrors = (error: any): FormikErrors<any> => {
    const data = error?.response?.data;
    const issues = data?.issues || data?.fieldErrors;
    if (!issues || typeof issues !== "object") return {};

    return Object.entries(issues).reduce<FormikErrors<any>>(
      (result, [field, messages]) => {
        const message = Array.isArray(messages) ? messages[0] : messages;
        if (message) result[field] = String(message);
        return result;
      },
      {}
    );
  };

  const handlerSubmit = async (
    params: any,
    { setErrors, setSubmitting }: FormikHelpers<any>
  ) => {
    setSubmitError("");

    try {
      const modalidadeId = Number(params.modalidadeId);
      const organizadorId = Number(params.organizadorId);
      const tipoServicoId = Number(params.tipoServicoId);
      const groupInsc = params.groupInsc;
      const exibirListInscrito = params.exibirListInscrito;

      if (
        modalidadeId <= 0 ||
        organizadorId <= 0 ||
        tipoServicoId <= 0 ||
        groupInsc === "" ||
        !exibirListInscrito
      ) {
        throw new Error(
          "As opções selecionadas não correspondem mais aos dados disponíveis. Recarregue a página."
        );
      }

      const dataEvento = new FormData();
      const append = (field: string, value: unknown) =>
        dataEvento.append(field, String(value ?? ""));

      append("titulo", params.titulo);
      append("limiteInscricao", params.limiteInscricao);
      append("dataEvento", params.dataEvento);
      append("horaLargada", params.horaLargada);
      append("inicioIncricao", params.inicioIncricao);
      append("horaInicioInsc", params.horaInicioInsc);
      append("fimInscricao", params.fimInscricao);
      append("horaFinalInsc", params.horaFinalInsc);
      append("valorServico", currencyToNumber(params.valorServico).toFixed(2));
      append(
        "taxaDescontoBoletoEq",
        Number(String(params.taxaDescontoBoletoEq).replace(",", ".")).toFixed(2)
      );
      append(
        "taxaInscricao",
        currencyToNumber(params.taxaInscricao).toFixed(2)
      );
      append("qtdParcelaCartao", params.qtdParcelaCartao);
      append("dataVencBoleto", params.dataVencBoleto);
      append("dataVencCartao", params.dataVencCartao);
      append("dataVencPix", params.dataVencPix);
      append("regulamento", params.regulamento);
      append("resumo", params.resumo);
      append("termoResp", params.termoResp);
      append("infoRetiradaKit", params.infoRetiradaKit);
      append("urlVideoDivulgacao", params.urlVideoDivulgacao);
      append("urlFacebookDivulgacao", params.urlFacebookDivulgacao);
      append("urlInstagramDivulgacao", params.urlInstagramDivulgacao);
      append("exibirMap", params.exibirMap);
      append("latitude", params.latitude);
      append("longitude", params.longitude);
      append("nomeDoLocal", params.nomeDoLocal);
      append("modalidadeId", modalidadeId);
      append("organizadorId", organizadorId);
      append("tipoServicoId", tipoServicoId);
      append("cep", params.cep);
      append("logradouro", params.logradouro);
      append("numero", params.numero);
      append("bairro", params.bairro);
      append("cidade", params.cidade);
      append("complemento", params.complemento);
      append("uf", String(params.uf).toUpperCase());
      append("groupInsc", groupInsc);
      append("exibirDestaque", params.exibirDestaque);
      append("isentarMorador", params.isentarMorador);
      append("exibirListInscrito", exibirListInscrito);

      fileBanner && dataEvento.append("urlBanner", fileBanner);
      fileCard && dataEvento.append("urlImageDestaque", fileCard);
      fileRegulamento && dataEvento.append("urlRegulamento", fileRegulamento);
      fileTermoResp && dataEvento.append("urlTermoResp", fileTermoResp);
      fileResumo && dataEvento.append("urlResumo", fileResumo);

      if (isEdit) {
        const statusId = Number(params.statusId);
        if (statusId <= 0) {
          throw new Error("O status selecionado não é mais válido.");
        }
        append("statusId", statusId);
      }

      const response = isEdit
        ? await editEventos(dataEvento, Number(evento.id))
        : await saveEventos(dataEvento);

      navigate(`${hrefEvento}/${response.data.slug}`);
      updateEvento();
      clearFiles();
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: isEdit
          ? "Alterações salvas com sucesso!"
          : "Evento cadastrado com sucesso!",
      });
    } catch (error: any) {
      const fieldErrors = getBackendFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
      }

      const message = resolveApiMessage(
        error?.response?.data,
        error?.message || "Não foi possível salvar o evento."
      );
      setSubmitError(message);
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  interface CoordsProps {
    lat: number;
    lng: number;
  }

  const handlerSetCoords = (
    coords: CoordsProps,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    setFieldValue("latitude", coords.lat);
    setFieldValue("longitude", coords.lng);
  };

  const handleFormSubmit = async (
    event: FormEvent<HTMLFormElement>,
    validateForm: () => Promise<Record<string, unknown>>,
    setTouched: (fields: Record<string, boolean>) => void,
    submitForm: () => void
  ) => {
    event.preventDefault();
    const formErrors = await validateForm();

    if (Object.keys(formErrors).length > 0) {
      setTouched(
        Object.keys(formErrors).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        )
      );

      const firstError = Object.values(formErrors)[0];
      setMessage(dispatch, {
        open: true,
        text: String(firstError),
        severity: "error",
      });
      return;
    }

    submitForm();
  };

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={valueForm}
        validationSchema={getSchemaCadastroEvento(Boolean(isEdit))}
        onSubmit={handlerSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          submitCount,
          validateForm,
          setTouched,
        }) => {
          return (
            <Form
              id={formCadastroEvento.formId}
              onSubmit={(event) =>
                handleFormSubmit(event, validateForm, setTouched, () =>
                  handleSubmit()
                )
              }
            >
              {optionsError && (
                <MDBox mb={2}>
                  <MDAlert color="error">{optionsError}</MDAlert>
                </MDBox>
              )}
              <BannerEvento
                eventoId={evento?.id}
                selectedFileBanner={handlerFileBanner}
                selectedFileCard={handlerFileCard}
                imageUrlBanner={imgUrlBanner}
                imageUrlCard={imgUrlCard}
              />

              {/* DADOS DO EVENTO */}
              <FieldDadosEvento
                values={values}
                handleBlur={handleBlur}
                handleChange={handleChange}
                listOrganizador={listOrganizador}
                listModalidade={listModalidade}
                listTipoServico={listTipoServico}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />

              {/* LIMITES E TIPO DE EVENTO */}
              <FieldDetails
                values={values}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handlerExibirDestaque={handlerExibirDestaque}
                checkedDestaque={destaque}
                listStatusEvento={listStatusEvento}
                isEdit={isEdit}
              />

              {/* TAXA ADMINISTRATIVA E VENCIMENTO */}
              <FieldTaxaAdm
                values={values}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                checkedIsentarMorador={isentarMorador}
                handlerIsentarMorador={handlerIsentarMorador}
              />

              {/* LINKS DE DIVULGAÇÃO EVENTO */}
              <FieldLinkDivulgacao
                values={values}
                handleBlur={handleBlur}
                handleChange={handleChange}
              />

              {/* REGULAMENTO DO EVENTO */}
              <FieldRegulamento
                eventoId={evento?.id}
                values={values}
                handlerDeleteFileReg={handlerDeleteFileReg}
                handlerFileReg={handlerFileReg}
                fileRegulamento={fileRegulamento}
                setFieldValue={setFieldValue}
              />

              {/* TERMO DE RESPONSABILIDADE */}
              <FieldTermoResp
                eventoId={evento?.id}
                values={values}
                handlerDeleteFileTerm={handlerDeleteFileTerm}
                handlerFileTerm={handlerFileTerm}
                fileTermoResp={fileTermoResp}
                setFieldValue={setFieldValue}
              />

              {/* INFORMAÇÃO COMPLEMENTAR DO EVENTO */}
              <FieldInfoComplementar
                eventoId={evento?.id}
                values={values}
                fileResumo={fileResumo}
                handlerDeleteFileResumo={handlerDeleteFileResumo}
                handlerFileResumo={handlerFileResumo}
                setFieldValue={setFieldValue}
              />

              {/* INFORMAÇÃO DO KITS */}
              <FieldInfokits
                values={values}
                handleBlur={handleBlur}
                handleChange={handleChange}
              />

              {/* MAPAS E ENDEREÇO LOCAL DO EVENTO */}
              <FieldMaps
                values={values}
                setFieldValue={setFieldValue}
                handlerSetCoords={handlerSetCoords}
                mapsExibir={mapsExibir}
                handlerExibirMaps={handlerExibirMaps}
                handleBlur={handleBlur}
                handleChange={handleChange}
              />

              {isEdit ? (
                <Card
                  ref={footerRef}
                  id="finnaly-register"
                  sx={{
                    transition:
                      "opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease",
                    opacity: footerInView ? 1 : 0,
                    transform: footerInView
                      ? "translateY(0)"
                      : "translateY(20px)",
                    boxShadow: footerInView ? 4 : 0,
                    pointerEvents: footerInView ? "auto" : "none",
                  }}
                >
                  <MDBox px={3} pt={3}>
                    <FormSubmitFeedback
                      errors={errors}
                      submitCount={submitCount}
                      submitError={submitError}
                      feedbackRef={submitFeedbackRef}
                    />
                  </MDBox>
                  <MDBox
                    pr={3}
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                  >
                    <MDBox p={3} lineHeight={1} pt={0}>
                      <MDBox mb={1}>
                        <MDTypography variant="h5">Editar Evento</MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                    >
                      <MDBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                        <MDButton
                          disabled={isSubmitting}
                          type="submit"
                          variant="gradient"
                          color="dark"
                          sx={{ height: "100%" }}
                        >
                          {isSubmitting ? (
                            <CircularProgress color="inherit" size="1.5rem" />
                          ) : null}
                          Salvar alterações
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              ) : (
                <Card id="finnaly-register">
                  <MDBox px={3} pt={3}>
                    <FormSubmitFeedback
                      errors={errors}
                      submitCount={submitCount}
                      submitError={submitError}
                      feedbackRef={submitFeedbackRef}
                    />
                  </MDBox>
                  <MDBox
                    pr={3}
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                  >
                    <MDBox p={3} lineHeight={1} pt={0}>
                      <MDBox mb={1}>
                        <MDTypography variant="h5">
                          Cadastro de Eventos
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="button" color="text">
                        Está quase lá para publicar seu evento. Após salvar,
                        defina as demais informações necessárias para a
                        publicação no site.
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                    >
                      <MDBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                        <MDButton
                          disabled={isSubmitting || isLoadingOptions}
                          type="submit"
                          variant="gradient"
                          color="dark"
                          sx={{ height: "100%" }}
                        >
                          {isSubmitting || isLoadingOptions ? (
                            <CircularProgress color="inherit" size="1.5rem" />
                          ) : null}
                          Salvar evento
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              )}

              {isEdit && (
                <MDBox
                  aria-hidden={footerInView}
                  sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    opacity: footerInView ? 0 : 1,
                    transform: footerInView
                      ? "translateY(16px) scale(0.92)"
                      : "translateY(0) scale(1)",
                    pointerEvents: footerInView ? "none" : "auto",
                    transition:
                      "opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <MDButton
                    disabled={isSubmitting}
                    type="submit"
                    variant="gradient"
                    color="dark"
                    sx={{
                      minWidth: 200,
                      boxShadow: 6,
                      px: 3,
                      py: 1.25,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress color="inherit" size="1.5rem" />
                    ) : null}
                    Salvar alterações
                  </MDButton>
                </MDBox>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

DadosEvento.defaultProps = {
  isEdit: false,
};
